import { createApi } from '@reduxjs/toolkit/query/react';

import handleApiError from '@/utils/handleApiError';

import axiosBaseQuery from '@/vendors/axiosBaseQuery';

import {
  fetchDistrictRolesWithSuperAdminBegin,
  fetchDistrictRolesWithSuperAdminFailed,
  fetchDistrictRolesWithSuperAdminSuccess
} from '@/store/actions/districtActions';
import {
  addUserToList,
  fetchUserByIdBegin,
  fetchUserByIdFailed,
  fetchUserByIdSuccess
} from '@/store/actions/userActions';

import {
  EUserTags,
  TAdminUserRolesParams,
  TBlockedUserGetParams,
  TBlockUserParams,
  TGlobalAuthUserParams,
  TUnblockUserParams,
  TUserByIdParams
} from '@/types/api-types/users';
import { TUserData } from '@/types/user-data';
import { TBlockedUser } from '@/types/responses/blocked-user';
import { AdminUserRolesResponse } from '@/types/responses/users';

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    EUserTags.ADMIN_USER_ROLES,
    EUserTags.USER_BY_ID,
    EUserTags.GLOBAL_USERS_DATA,
    EUserTags.BLOCKED_USERS
  ],
  endpoints: builder => ({
    fetchDistrictRolesWithSuperAdmin: builder.query<
      AdminUserRolesResponse[],
      TAdminUserRolesParams
    >({
      query: ({ district }) => ({
        method: 'GET',
        url: `${district}/user_roles/?with_admin=true`
      }),
      onQueryStarted: async ({ district }, { dispatch, queryFulfilled }) => {
        dispatch(fetchDistrictRolesWithSuperAdminBegin());
        try {
          const { data } = await queryFulfilled;
          dispatch(fetchDistrictRolesWithSuperAdminSuccess(district, data));
        } catch (error) {
          dispatch(fetchDistrictRolesWithSuperAdminFailed());
          handleApiError(error, dispatch);
        }
      },
      providesTags: data =>
        data
          ? [
              ...data.map(({ id }) => ({
                type: EUserTags.ADMIN_USER_ROLES,
                id
              })),
              {
                type: EUserTags.ADMIN_USER_ROLES,
                id: EUserTags.ADMIN_USER_ROLES
              }
            ]
          : [
              {
                type: EUserTags.ADMIN_USER_ROLES,
                id: EUserTags.ADMIN_USER_ROLES
              }
            ]
    }),
    fetchUserById: builder.query<TUserData, TUserByIdParams>({
      query: ({ district, id }) => ({
        method: 'GET',
        url: `${district}/auth/users/${id}/`
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        dispatch(fetchUserByIdBegin());

        try {
          const { data } = await queryFulfilled;
          dispatch(fetchUserByIdSuccess(data));
          dispatch(addUserToList(data));
        } catch (error) {
          dispatch(fetchUserByIdFailed());
          handleApiError(error, dispatch);
        }
      },
      providesTags: data =>
        data
          ? [
              { type: EUserTags.USER_BY_ID, id: data.id },
              {
                type: EUserTags.USER_BY_ID,
                id: EUserTags.USER_BY_ID
              }
            ]
          : [
              {
                type: EUserTags.USER_BY_ID,
                id: EUserTags.USER_BY_ID
              }
            ]
    }),
    fetchAuthUsersData: builder.query<TUserData, TGlobalAuthUserParams>({
      query: ({ userId }) => ({
        method: 'GET',
        url: `auth/users/${userId}/`
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        dispatch(fetchUserByIdBegin());

        try {
          const { data } = await queryFulfilled;
          dispatch(fetchUserByIdSuccess(data));
          dispatch(addUserToList(data));
        } catch (error) {
          dispatch(fetchUserByIdFailed());
          handleApiError(error, dispatch);
        }
      },
      providesTags: data =>
        data
          ? [
              {
                type: EUserTags.GLOBAL_USERS_DATA,
                id: data.id
              },
              {
                type: EUserTags.GLOBAL_USERS_DATA,
                id: EUserTags.GLOBAL_USERS_DATA
              }
            ]
          : [
              {
                type: EUserTags.GLOBAL_USERS_DATA,
                id: EUserTags.GLOBAL_USERS_DATA
              }
            ]
    }),
    fetchBlockedUsers: builder.query<TBlockedUser[], TBlockedUserGetParams>({
      query: ({ district }) => ({
        method: 'GET',
        url: `${district}/user_blocked_status/`,
        params: {
          pagination_false: true
        }
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          handleApiError(error, dispatch);
        }
      },
      providesTags: data =>
        data
          ? [
              ...data.map(({ id }) => ({
                type: EUserTags.BLOCKED_USERS,
                id
              })),
              {
                type: EUserTags.BLOCKED_USERS,
                id: EUserTags.BLOCKED_USERS
              }
            ]
          : [
              {
                type: EUserTags.BLOCKED_USERS,
                id: EUserTags.BLOCKED_USERS
              }
            ]
    }),
    blockUser: builder.mutation<TBlockedUser, TBlockUserParams>({
      query: ({ userId: user_id, district }) => ({
        method: 'POST',
        url: `${district}/user_blocked_status/`,
        data: { user_id }
      }),
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            usersApi.util.updateQueryData(
              'fetchBlockedUsers',
              { district: arg.district },
              draft => {
                draft.push(data);

                return draft;
              }
            )
          );
        } catch (error) {
          handleApiError(error, dispatch);
        }
      }
    }),
    unblockUser: builder.mutation<undefined, TUnblockUserParams>({
      query: ({ id, district }) => ({
        method: 'DELETE',
        url: `${district}/user_blocked_status/${id}/`
      }),
      onQueryStarted: async (
        { id, district },
        { dispatch, queryFulfilled }
      ) => {
        try {
          await queryFulfilled;

          dispatch(
            usersApi.util.updateQueryData(
              'fetchBlockedUsers',
              { district },
              draft => draft.filter(item => item.id !== id)
            )
          );
        } catch (error) {
          handleApiError(error, dispatch);
        }
      }
    })
  }),
  keepUnusedDataFor: 1200
});

export const {
  useLazyFetchDistrictRolesWithSuperAdminQuery,
  useLazyFetchUserByIdQuery,
  useLazyFetchAuthUsersDataQuery,
  useFetchBlockedUsersQuery,
  useBlockUserMutation,
  useUnblockUserMutation
} = usersApi;
